import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FileOutput, Plus, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CUR, lineTotal, buildInvoiceDoc } from "./invoicePdf";
import { BaseAddressSelect } from "./BaseAddressSelect";

const today = () => new Date().toISOString().slice(0, 10);
const userName = (u) => (u?.first_name && u?.last_name ? `${u.first_name} ${u.last_name}` : u?.display_name || "");

const DEFAULTS = {
  shipper: "Summit Air\n100 Dickins Street\nYellowknife NT X1A 3T2 Canada",
  consignee: "",
  notifyParty: "",
  date: today(),
  invoiceNo: "",
  customerPo: "",
  dateExport: today(),
  origin: "Canada",
  blAwb: "",
  finalDest: "",
  exportRoute: "",
  termsSale: "",
  termsPayment: "",
  freightTerms: "prepaid",
  marks: "",
  currency: "USD",
  countryManufacture: "",
  handling: "",
  freight: "",
  misc: "",
  certify: "\"WE HEREBY CERTIFY THIS INVOICE TO BE TRUE AND CORRECT.\"",
  signedName: "",
  signedTitle: "Stores",
  signedDate: today(),
  exportedTo: "",
};

const blankItem = () => ({ qty: "", desc: "", hs: "", unit: "" });

const F = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function CommercialInvoice() {
  const { user } = useAuth();
  const [m, setM] = useState(() => ({ ...DEFAULTS, signedName: userName(user) }));
  const [items, setItems] = useState([blankItem()]);
  const set = (k) => (e) => setM((s) => ({ ...s, [k]: e.target?.value ?? e }));
  const setItem = (i, k) => (e) => setItems((its) => its.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)));

  const subtotal = items.reduce((s, it) => s + (lineTotal(it) || 0), 0);
  const total = subtotal + (Number(m.handling) || 0) + (Number(m.freight) || 0) + (Number(m.misc) || 0);
  const sym = CUR[m.currency].sym;
  const fmt = (n) => `${sym}${(Number(n) || 0).toFixed(2)}`;

  const reset = () => {
    setM({ ...DEFAULTS, date: today(), dateExport: today(), signedDate: today(), signedName: userName(user) });
    setItems([blankItem()]);
  };

  const exportPdf = async () => {
    try {
      const doc = await buildInvoiceDoc(m, items);
      doc.save(`Commercial Invoice${m.invoiceNo ? ` ${m.invoiceNo}` : ""} - ${m.date}.pdf`);
      toast.success("PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  const inputCls = "h-9";
  return (
    <div data-testid="commercial-invoice-form">
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={reset} data-testid="fg-reset-btn">
          <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
        </Button>
        <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={exportPdf} data-testid="fg-export-btn">
          <FileOutput className="w-4 h-4 mr-1.5" /> Export PDF
        </Button>
      </div>

      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        <div className="border border-summit-hairline rounded-lg bg-white p-4 space-y-3">
          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Shipper / Exporter</label>
              <BaseAddressSelect
                testId="fg-shipper-autofill"
                onSelect={(c) => setM((s) => ({ ...s, shipper: ["Summit Air", ...String(c.address || "").split(",").map((p) => p.trim()).filter(Boolean)].join("\n") }))}
              />
            </div>
            <div className="mt-1">
              <Textarea value={m.shipper} onChange={set("shipper")} rows={4} className="text-sm" data-testid="fg-shipper" />
            </div>
          </div>
          <F label="Consignee">
            <Textarea value={m.consignee} onChange={set("consignee")} rows={4} placeholder={"Company name\nAddress\nCity, Prov/State, Postal\nCountry   Phone / Fax"} className="text-sm" data-testid="fg-consignee" />
          </F>
          <F label="Notify Party / Intermediate Consignee">
            <Textarea value={m.notifyParty} onChange={set("notifyParty")} rows={3} className="text-sm" data-testid="fg-notify-party" />
          </F>
          <F label="Marks">
            <Textarea value={m.marks} onChange={set("marks")} rows={2} className="text-sm" data-testid="fg-marks" />
          </F>
        </div>
        <div className="border border-summit-hairline rounded-lg bg-white p-4 grid grid-cols-2 gap-3 content-start">
          <F label="Date"><Input className={inputCls} type="date" value={m.date} onChange={set("date")} data-testid="fg-date" /></F>
          <F label="Commercial Invoice No."><Input className={inputCls} value={m.invoiceNo} onChange={set("invoiceNo")} placeholder="e.g. CI-2026-014" data-testid="fg-invoice-no" /></F>
          <F label="Customer P.O. Number"><Input className={inputCls} value={m.customerPo} onChange={set("customerPo")} data-testid="fg-customer-po" /></F>
          <F label="Date of Export"><Input className={inputCls} type="date" value={m.dateExport} onChange={set("dateExport")} data-testid="fg-date-export" /></F>
          <F label="Country of Origin"><Input className={inputCls} value={m.origin} onChange={set("origin")} data-testid="fg-origin" /></F>
          <F label="B/L / AWB Number"><Input className={inputCls} value={m.blAwb} onChange={set("blAwb")} data-testid="fg-bl-awb" /></F>
          <F label="Final Destination"><Input className={inputCls} value={m.finalDest} onChange={set("finalDest")} data-testid="fg-final-dest" /></F>
          <F label="Export Route / Carrier"><Input className={inputCls} value={m.exportRoute} onChange={set("exportRoute")} data-testid="fg-export-route" /></F>
          <F label="Terms of Sale"><Input className={inputCls} value={m.termsSale} onChange={set("termsSale")} data-testid="fg-terms-sale" /></F>
          <F label="Terms of Payment"><Input className={inputCls} value={m.termsPayment} onChange={set("termsPayment")} placeholder="e.g. N30" data-testid="fg-terms-payment" /></F>
          <F label="Freight">
            <div className="flex items-center gap-4 h-9">
              {["prepaid", "collect"].map((v) => (
                <label key={v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="freightTerms"
                    checked={m.freightTerms === v}
                    onChange={() => setM((s) => ({ ...s, freightTerms: v }))}
                    data-testid={`fg-freight-${v}`}
                  />
                  <span className="font-mono text-[11px] uppercase tracking-widest">{v}</span>
                </label>
              ))}
            </div>
          </F>
          <F label="Currency">
            <select className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" value={m.currency} onChange={set("currency")} data-testid="fg-currency">
              {Object.keys(CUR).map((c) => <option key={c}>{c}</option>)}
            </select>
          </F>
        </div>
      </div>

      <div className="mt-4 border border-summit-hairline rounded-lg bg-white overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-summit-hairline bg-summit-canvas">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Line items</span>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setItems((its) => [...its, blankItem()])} data-testid="fg-add-item-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add item
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-summit-hairline">
                {["Quantity", "Description (multi-line supported)", "H.S. Number", `Unit price (${sym})`, "Total price", ""].map((h) => (
                  <th key={h} className="text-left px-2 py-1.5 font-mono text-[9px] uppercase tracking-widest text-gray-500 first:pl-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const t = lineTotal(it);
                return (
                  <tr key={i} className="border-b border-summit-hairline last:border-0 align-top" data-testid={`fg-item-row-${i}`}>
                    <td className="pl-4 pr-1 py-1.5 w-28"><Input className="h-8 text-xs" value={it.qty} onChange={setItem(i, "qty")} placeholder="e.g. 1 Crate" data-testid={`fg-item-qty-${i}`} /></td>
                    <td className="px-1 py-1.5"><Textarea rows={2} className="text-xs min-h-[32px]" value={it.desc} onChange={setItem(i, "desc")} placeholder={"HC132H0010-000 - Support Frame\nW&D: 130 x 89 x 34 cm - 79kg"} data-testid={`fg-item-desc-${i}`} /></td>
                    <td className="px-1 py-1.5 w-32"><Input className="h-8 text-xs" value={it.hs} onChange={setItem(i, "hs")} placeholder="88033000" data-testid={`fg-item-hs-${i}`} /></td>
                    <td className="px-1 py-1.5 w-28"><Input className="h-8 text-xs" type="number" min="0" step="0.01" value={it.unit} onChange={setItem(i, "unit")} data-testid={`fg-item-price-${i}`} /></td>
                    <td className="px-1 py-1.5 w-28 font-mono text-xs text-right whitespace-nowrap pt-3.5">{t === null ? "—" : fmt(t)}</td>
                    <td className="px-1 py-1.5 w-8">
                      <button className="p-1 rounded hover:bg-rose-50 text-rose-400" disabled={items.length === 1} onClick={() => setItems((its) => its.filter((_, j) => j !== i))} data-testid={`fg-item-remove-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap justify-end gap-3 px-4 py-3 border-t border-summit-hairline bg-summit-canvas items-end">
          <F label="Country of Manufacture"><Input className="h-8 w-40 text-xs" value={m.countryManufacture} onChange={set("countryManufacture")} placeholder="e.g. United Kingdom" data-testid="fg-country-manufacture" /></F>
          <F label="Handling"><Input className="h-8 w-28 text-xs" type="number" min="0" step="0.01" value={m.handling} onChange={set("handling")} data-testid="fg-handling" /></F>
          <F label="Freight"><Input className="h-8 w-28 text-xs" type="number" min="0" step="0.01" value={m.freight} onChange={set("freight")} data-testid="fg-freight" /></F>
          <F label="Misc."><Input className="h-8 w-28 text-xs" type="number" min="0" step="0.01" value={m.misc} onChange={set("misc")} data-testid="fg-misc" /></F>
          <div className="text-right pl-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Subtotal {fmt(subtotal)} · Total</div>
            <div className="font-mono text-2xl tracking-tighter" data-testid="fg-total">{m.currency} {fmt(total)}</div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        <div className="border border-summit-hairline rounded-lg bg-white p-4 space-y-3">
          <F label="Certification statement">
            <Textarea value={m.certify} onChange={set("certify")} rows={2} className="text-sm" data-testid="fg-certify" />
          </F>
          <F label="Exported from the United States to (footer)">
            <Input className={inputCls} value={m.exportedTo} onChange={set("exportedTo")} data-testid="fg-exported-to" />
          </F>
        </div>
        <div className="border border-summit-hairline rounded-lg bg-white p-4 grid grid-cols-2 gap-3 content-start">
          <F label="Signed by (print name)"><Input className={inputCls} value={m.signedName} onChange={set("signedName")} placeholder="e.g. John Simon" data-testid="fg-signed-name" /></F>
          <F label="Title"><Input className={inputCls} value={m.signedTitle} onChange={set("signedTitle")} data-testid="fg-signed-title" /></F>
          <F label="Date"><Input className={inputCls} type="date" value={m.signedDate} onChange={set("signedDate")} data-testid="fg-signed-date" /></F>
        </div>
      </div>
    </div>
  );
}
